/**
 * Price engine. Pure functions, no React and no data access — this is the piece
 * that has to stay correct, so it is unit-tested in pricing.test.ts.
 *
 * Order of operations:
 *   1. resolve a nightly rate for every night of the stay
 *   2. add extra-person charges above the unit's base occupancy
 *   3. add fees (cleaning, linen, pets, …)
 *   4. add tourist tax, which is a pass-through levy and priced per liable person-night
 *   5. apply the VAT scheme
 */

import type { Fee, ISODate, RatePeriod, Unit, VatScheme } from '../data/model'
import { ageOn, eachDate, isoWeekday, nights } from './dates'
import { fromCents, roundCents, splitGross, toCents, vatOf } from './money'

export interface TouristTaxRule {
  id: string
  municipality: string
  name: Record<string, string>
  amount_per_person_night: number
  vat_rate: number
  exempt_under_age: number | null
  reduced_under_age: number | null
  reduced_amount: number | null
  max_nights: number | null
}

export interface PricingInput {
  unit: Unit
  ratePeriods: RatePeriod[]
  fees: Fee[]
  touristTax?: TouristTaxRule | null
  arrival: ISODate
  departure: ISODate
  adults: number
  children: number
  /** Ages at arrival. Needed for tourist-tax exemptions; may be empty. */
  childrenAges?: number[]
  vatScheme: VatScheme
  vatRateAccommodation: number
  /**
   * Whether the amounts the landlord typed already contain VAT.
   *
   * A holiday let advertises "€95 a night" meaning what the guest pays, so this is
   * on for every new tenant. With it off, €95 is the net rate and the guest is
   * invoiced €104.50 at 10 % — correct arithmetic, and not what most landlords
   * meant. It applies to every amount they enter: rates, extra person, fees, the
   * tourist tax, the discount.
   */
  pricesIncludeVat?: boolean
  /**
   * Ids of non-mandatory fees the guest actually booked. Optional fees are never
   * charged by default — billing someone for a pet they do not have is the kind
   * of error that costs a landlord the booking.
   */
  optionalFeeIds?: string[]
  /** Manual discount in the unit's currency, applied to the net accommodation total. */
  discount?: number
  discountLabel?: string
  /**
   * Der Grund für den Nachlass, so wie der Vermieter ihn aufgeschrieben hat
   * („Gutschein", „Stammgast 10 %"). Steht er da, ist er der Text der Nachlasszeile —
   * auf dem Bildschirm und auf der Rechnung, unübersetzt.
   *
   * Unübersetzt, weil ein frei getippter Grund nichts ist, was sich übersetzen ließe,
   * und weil ein Gast, der „Gutschein" liest, genau das wiedererkennen soll, was ihm
   * am Telefon zugesagt wurde. Ohne Grund bleibt es beim übersetzten „Rabatt".
   */
  discountReason?: string | null
  /**
   * Ein für DIESEN Aufenthalt vereinbarter Nachtpreis, der die Preisliste ersetzt.
   *
   * WARUM ES IHN BRAUCHT: „die Stammkundin zahlt 100 statt 129" ist keine Saison und
   * keine Regel, sondern eine einzelne Zusage. Sie in die Preisliste zu schreiben
   * träfe alle anderen mit, und `price_locked` (die Summe einfrieren) wäre zu grob:
   * verschiebt sich der Aufenthalt um eine Nacht, soll der vereinbarte Nachtpreis
   * weitergelten und die Summe NEU gerechnet werden.
   *
   * WAS ER NICHT BERÜHRT: Ortstaxe, Nebenkosten, Zusatzpersonen und die im Grundpreis
   * enthaltene Belegung kommen weiter aus Preisliste und Stammdaten. Vereinbart wurde
   * der Preis der Übernachtung, nicht der Erlass der Kurabgabe.
   *
   * 0 ist erlaubt (die geschenkte Nacht), negative Werte werden ignoriert.
   */
  nightlyOverride?: number | null
  /**
   * Ermäßigung für mitreisende Kinder auf den Zusatzpersonenpreis. Leer = keine;
   * dann zahlt jede Person über der Grundbelegung denselben Aufpreis.
   */
  childRates?: ChildRate[]
  /** Worauf die Ermäßigung wirkt. Vorgabe: nur der Aufpreis je Zusatzperson. */
  childRateScope?: ChildRateScope
}

export interface PricedNight {
  date: ISODate
  /** Nightly rate in the tenant currency. */
  price: number
  rateName: string | null
}

export type LineKind = 'accommodation' | 'fee' | 'extra' | 'discount'

export interface PriceLine {
  kind: LineKind
  code: string
  /** Translation key (`fee.cleaning`) or `fee:<code>` when `nameJson` carries the label. */
  label: string
  /** Localised label straight from a jsonb column, when the record has one. */
  nameJson?: Record<string, string>
  qty: number
  unitPrice: number
  net: number
  vatRate: number
  vat: number
  gross: number
}

export type PricingWarning =
  | { code: 'min_nights'; nights: number }
  | { code: 'max_occupancy'; max: number }
  | { code: 'no_rate' }
  | { code: 'unknown_child_ages' }

export interface PriceResult {
  nights: PricedNight[]
  lines: PriceLine[]
  totals: { net: number; vat: number; gross: number }
  nightCount: number
  averageNightly: number
  /**
   * Was die Nacht im Schnitt nach Preisliste kostete — ohne den vereinbarten
   * Sonderpreis. Gleich `averageNightly`, solange nichts überschrieben ist.
   *
   * Die Maske zeigt damit an, WOVON abgewichen wird: ein Sonderpreis, neben dem der
   * Listenpreis nicht steht, lässt sich weder prüfen noch später erklären.
   */
  listAverageNightly: number
  /** Der vereinbarte Nachtpreis, wenn einer gilt — sonst null. */
  nightlyOverride: number | null
  warnings: PricingWarning[]
}

/** Picks the rate that applies to one night: most specific, then highest priority. */
export function resolveNightlyRate(
  unit: Unit,
  periods: RatePeriod[],
  date: ISODate,
): { price: number; rate: RatePeriod | null; baseOccupancy: number; extraPerson: number } {
  const dow = isoWeekday(date)
  const applies = (p: RatePeriod) =>
    p.unit_id === unit.id ||
    (p.unit_id == null && p.category_id != null && p.category_id === unit.category_id) ||
    (p.unit_id == null && p.category_id == null)

  const candidates = periods.filter(
    (p) =>
      p.active &&
      applies(p) &&
      p.valid_from <= date &&
      p.valid_to >= date &&
      (p.dow_mask & (1 << dow)) !== 0,
  )

  // Most specific scope wins first: an exception written for one unit must never be
  // overridden by a category or house-wide rule, whatever their priorities say.
  const scope = (p: RatePeriod) =>
    p.unit_id != null ? 2 : p.category_id != null ? 1 : 0

  candidates.sort((a, b) => {
    if (scope(b) !== scope(a)) return scope(b) - scope(a)
    if (b.priority !== a.priority) return b.priority - a.priority
    return b.valid_from.localeCompare(a.valid_from)
  })

  const best = candidates[0] ?? null
  return {
    price: best ? best.price_per_night : unit.base_price,
    rate: best,
    baseOccupancy: best?.base_occupancy ?? unit.base_occupancy,
    extraPerson: best?.extra_person_price ?? unit.extra_person_price,
  }
}

function feeQty(basis: Fee['basis'], nightCount: number, persons: number): number {
  switch (basis) {
    case 'per_stay': return 1
    case 'per_night': return nightCount
    case 'per_person': return persons
    case 'per_person_night': return nightCount * persons
    case 'percent': return 1
  }
}

/** How many guests owe tourist tax, given the exemption bracket. */
export function liableGuests(
  adults: number,
  childrenAges: number[],
  childrenCount: number,
  exemptUnderAge: number | null,
): { liable: number; reduced: number; agesUnknown: boolean } {
  if (exemptUnderAge === null) {
    return { liable: adults + childrenCount, reduced: 0, agesUnknown: false }
  }
  // Without ages we cannot prove an exemption, so we charge — under-charging a
  // municipality is the landlord's problem, over-charging is visible and fixable.
  if (childrenAges.length !== childrenCount) {
    return { liable: adults + childrenCount, reduced: 0, agesUnknown: childrenCount > 0 }
  }
  const exempt = childrenAges.filter((a) => a < exemptUnderAge).length
  return { liable: adults + childrenCount - exempt, reduced: 0, agesUnknown: false }
}

/**
 * Kinderermäßigung auf den Zusatzpersonenpreis.
 *
 * WAS SIE BETRIFFT: nur den Aufpreis je Person über der Belegung, die im Grundpreis
 * enthalten ist. Der Grundpreis selbst bleibt, was er ist — eine Wohnung kostet
 * dasselbe, ob das dritte Bett von einem Kind oder von einer Erwachsenen benutzt wird,
 * und genau darum rechnen Vermieter die Ermäßigung auf den Aufpreis.
 *
 * WIE SIE GELESEN WIRD: aufsteigend nach `upToAge`, und `upToAge` ist AUSSCHLIESSLICH.
 * `{ upToAge: 6, percent: 0 }` heißt „bis einschließlich 5 Jahre frei" — so, wie es
 * die Prospekte schreiben („Kinder unter 6 Jahren kostenlos"). Wer älter ist als die
 * letzte Stufe, zahlt den vollen Aufpreis.
 */
export interface ChildRate {
  /** Unter diesem Alter gilt die Stufe. 6 = bis einschließlich 5 Jahre. */
  upToAge: number
  /** Anteil des Preises in Prozent, den diese Altersstufe zahlt. 0 = kostenfrei. */
  percent: number
}

/**
 * Worauf die Ermäßigung wirkt. Die beiden Fälle sind zwei verschiedene Betriebe.
 *
 * `surcharge` — die Ferienwohnung. Der Preis gilt für die Wohnung; im Grundpreis
 * stecken zwei oder vier Plätze, und erst darüber kostet jede Person extra. Nur
 * dieser Aufpreis wird ermäßigt: die Wohnung kostet dasselbe, ob im dritten Bett ein
 * Kind schläft oder eine Erwachsene.
 *
 * `nightly` — das Zimmer mit Personenpreis, also Pension, Frühstückspension,
 * Doppelzimmer. Dort kostet die Übernachtung pro Person, und „Kinder zahlen 50 %"
 * heißt: die Hälfte des Übernachtungsanteils. Der Nächtigungspreis wird deshalb durch
 * die im Grundpreis enthaltene Belegung geteilt, und der Anteil des Kindes ermäßigt.
 * Leere Plätze kosten weiter voll — wer allein anreist, bekommt kein halbes Zimmer.
 */
export type ChildRateScope = 'surcharge' | 'nightly'

/** Der Anteil, den eine Person dieses Alters vom Zusatzpersonenpreis zahlt. */
export function childFactor(age: number, rates: ChildRate[]): number {
  const band = [...rates]
    .sort((a, b) => a.upToAge - b.upToAge)
    .find((r) => age < r.upToAge)
  return band ? Math.max(0, Math.min(100, band.percent)) / 100 : 1
}

/**
 * Die Reisegruppe, sortiert nach dem, was sie kostet — teuerste zuerst.
 *
 * Die Reihenfolge entscheidet mit: die im Grundpreis enthaltenen Plätze werden von
 * vorne aufgefüllt, also von den Erwachsenen und den ältesten Kindern. Ein Kleinkind
 * landet damit garantiert unter den Zusatzpersonen und bekommt seine Ermäßigung.
 * Andersherum — Kinder zuerst in den Grundpreis — wäre die Ermäßigung in dem Moment
 * wertlos, in dem sie greifen soll.
 */
export interface PartyMember {
  /** Anteil am Zusatzpersonenpreis. 1 = voll. */
  factor: number
  /** Die Altersstufe, aus der der Anteil stammt. Fehlt bei Erwachsenen. */
  band?: ChildRate
}

export function partyMembers(
  adults: number, childrenAges: number[], rates: ChildRate[],
): PartyMember[] {
  const sorted = [...rates].sort((a, b) => a.upToAge - b.upToAge)
  const children = [...childrenAges]
    .sort((a, b) => b - a)
    .map((age) => {
      const band = sorted.find((r) => age < r.upToAge)
      return band
        ? { factor: Math.max(0, Math.min(100, band.percent)) / 100, band }
        : { factor: 1 }
    })
  return [
    ...Array.from({ length: Math.max(0, adults) }, () => ({ factor: 1 })),
    ...children,
  ]
}

/** Ein Schlüssel je Altersstufe; Erwachsene teilen sich einen. */
function bandKey(m: PartyMember): string {
  return m.band ? `child:${m.band.upToAge}:${m.band.percent}` : 'adult'
}

/**
 * Die Zeile auf dem Beleg, in beiden Sprachen.
 *
 * „Kinder unter 6 Jahren" und nicht „Kinder 0–5": so steht es in jedem Prospekt, und
 * der Gast soll den Satz wiedererkennen, den er beim Buchen gelesen hat.
 */
function childBandLabel(band: ChildRate): Record<string, string> {
  const free = band.percent <= 0
  return {
    de: free
      ? `Kinder unter ${band.upToAge} Jahren (kostenfrei)`
      : `Kinder unter ${band.upToAge} Jahren (${Math.round(band.percent)} %)`,
    en: free
      ? `Children under ${band.upToAge} (free of charge)`
      : `Children under ${band.upToAge} (${Math.round(band.percent)}%)`,
  }
}

/** Nur die Anteile — für Tests und alles, was die Stufe nicht braucht. */
export function partyFactors(
  adults: number, childrenAges: number[], rates: ChildRate[],
): number[] {
  return partyMembers(adults, childrenAges, rates).map((m) => m.factor)
}

export function priceStay(input: PricingInput): PriceResult {
  const {
    unit, ratePeriods, fees, touristTax, arrival, departure,
    adults, children, childrenAges = [], vatScheme, vatRateAccommodation,
    discount = 0, discountLabel = 'fee.discount', discountReason, optionalFeeIds = [],
    pricesIncludeVat = false,
  } = input

  // Ein Sonderpreis von 0 ist eine Aussage („diese Nacht geht aufs Haus"), ein
  // negativer ist ein Vertipper. Der eine gilt, der andere wird verworfen.
  const nightlyOverride =
    input.nightlyOverride != null && input.nightlyOverride >= 0 ? input.nightlyOverride : null

  const warnings: PricingWarning[] = []
  const nightCount = nights(arrival, departure)
  const persons = adults + children
  const noVat = vatScheme !== 'standard'

  /*
   * Wer wieviel vom Zusatzpersonenpreis zahlt.
   *
   * Ohne Altersangaben lässt sich keine Ermäßigung begründen, also zahlt jede Person
   * voll — und der Vermieter sieht die Warnung, dass Alter fehlen. Lieber ein zu
   * hoher Vorschlag, den er selbst korrigiert, als eine stille Ermäßigung, die er
   * dem Gast nie versprochen hat.
   */
  const childRates = input.childRates ?? []
  const childScope: ChildRateScope = input.childRateScope ?? 'surcharge'
  const agesKnown = children === 0 || childrenAges.length === children
  const party: PartyMember[] = agesKnown
    ? partyMembers(adults, childrenAges, childRates)
    : Array.from({ length: persons }, () => ({ factor: 1 }))
  if (childRates.length > 0 && !agesKnown) {
    warnings.push({ code: 'unknown_child_ages' })
  }

  if (nightCount <= 0) {
    return {
      nights: [], lines: [], nightCount: 0, averageNightly: 0,
      listAverageNightly: 0, nightlyOverride: null,
      totals: { net: 0, vat: 0, gross: 0 }, warnings,
    }
  }

  // ---- 1 & 2: accommodation, night by night
  const pricedNights: PricedNight[] = []
  let accommodationCents = 0
  /** Dieselben Nächte nach Preisliste — nur als Vergleichswert für die Maske. */
  let listCents = 0
  let extraPersonCents = 0
  /** Zusatzpersonen nach Altersstufe getrennt — je Stufe eine eigene Zeile. */
  const extraBuckets = new Map<string,
    { cents: number; personNights: number; member: PartyMember }>()
  /** Nachlass auf die Übernachtung beim Personenpreis, ebenfalls je Altersstufe. */
  let childDiscountCents = 0
  const discountBuckets = new Map<string,
    { cents: number; personNights: number; member: PartyMember }>()
  let minNightsRequired = unit.min_nights
  let sawRate = false

  for (const date of eachDate(arrival, departure)) {
    const r = resolveNightlyRate(unit, ratePeriods, date)
    if (r.rate) {
      sawRate = true
      if (r.rate.min_nights && r.rate.min_nights > minNightsRequired) {
        minNightsRequired = r.rate.min_nights
      }
    }
    // Der Sonderpreis wirkt auf JEDE Nacht des Aufenthalts, auch auf die, die nach
    // Preisliste teurer oder billiger wären. Zugesagt wurde „100 die Nacht", nicht
    // „100 statt 129 und sonst wie gehabt" — eine Wochenendnacht zum Listenpreis
    // mitten im Sonderpreis wäre genau die Überraschung, die den Gast anrufen lässt.
    const nightPrice = nightlyOverride ?? r.price
    pricedNights.push({ date, price: nightPrice, rateName: r.rate?.name ?? null })
    accommodationCents += toCents(nightPrice)
    listCents += toCents(r.price)

    /*
     * Beim Personenpreis (Pension, Doppelzimmer) wirkt die Ermäßigung auf die
     * Übernachtung selbst: der Nächtigungspreis wird durch die enthaltene Belegung
     * geteilt, und vom Anteil des Kindes wird abgezogen.
     *
     * Abgezogen und nicht kleingerechnet: die Zeile „Übernachtung" behält den Preis,
     * den der Gast im Prospekt gelesen hat, und die Ermäßigung steht als eigener
     * Posten darunter. Ein stillschweigend reduzierter Nächtigungspreis sieht auf der
     * Rechnung wie ein Tippfehler aus — und die Ermäßigung, für die der Gast sich
     * entschieden hat, wäre unsichtbar.
     */
    if (childScope === 'nightly' && r.baseOccupancy > 0) {
      const perPersonCents = toCents(nightPrice) / r.baseOccupancy
      for (const member of party.slice(0, r.baseOccupancy)) {
        if (member.factor >= 1) continue
        const off = roundCents(perPersonCents * (1 - member.factor))
        if (off <= 0) continue
        childDiscountCents += off
        const key = bandKey(member)
        const bucket = discountBuckets.get(key) ?? { cents: 0, personNights: 0, member }
        bucket.cents += off
        bucket.personNights += 1
        discountBuckets.set(key, bucket)
      }
    }

    // Die im Grundpreis enthaltenen Plätze werden von vorne aufgefüllt — Erwachsene
    // und ältere Kinder zuerst. Alles darüber kostet extra, jede Person mit ihrem
    // eigenen Anteil. Der Aufpreis wird in BEIDEN Betriebsarten ermäßigt: er kostet
    // je Person, also gilt der Anteil des Kindes auch dort.
    if (r.extraPerson > 0) {
      for (const member of party.slice(r.baseOccupancy)) {
        const cents = roundCents(toCents(r.extraPerson) * member.factor)
        extraPersonCents += cents
        const key = bandKey(member)
        const bucket = extraBuckets.get(key)
          ?? { cents: 0, personNights: 0, member }
        bucket.cents += cents
        bucket.personNights += 1
        extraBuckets.set(key, bucket)
      }
    }
  }

  if (!sawRate && unit.base_price === 0) warnings.push({ code: 'no_rate' })
  if (nightCount < minNightsRequired) {
    warnings.push({ code: 'min_nights', nights: minNightsRequired })
  }
  if (persons > unit.max_occupancy) {
    warnings.push({ code: 'max_occupancy', max: unit.max_occupancy })
  }

  const accVat = noVat ? 0 : vatRateAccommodation
  const lines: PriceLine[] = []

  const avgNightly = fromCents(roundCents(accommodationCents / nightCount))
  lines.push(makeLine({
    kind: 'accommodation',
    code: 'accommodation',
    label: 'fee.accommodation',
    qty: nightCount,
    unitPrice: avgNightly,
    amountCents: accommodationCents,
    vatRate: accVat,
    inclusive: pricesIncludeVat,
  }))

  /*
   * Je Altersstufe eine eigene Zeile.
   *
   * Auch die kostenfreie: „2 Kinder unter 6 Jahren · € 0,00" ist die Antwort auf die
   * Frage, die eine Familie beim Lesen der Rechnung als Erstes stellt. Eine Null, die
   * dasteht, beruhigt; eine Null, die fehlt, sieht aus wie ein Fehler oder wie ein
   * Posten, den man vergessen hat zu berechnen.
   */
  // Der Nachlass auf die Übernachtung, je Altersstufe, direkt unter der
  // Übernachtungszeile — dort sucht ihn ein Gast, der nachrechnet.
  for (const bucket of [...discountBuckets.values()].sort((a, b) => a.member.factor - b.member.factor)) {
    const band = bucket.member.band
    if (!band || bucket.cents <= 0) continue
    lines.push(makeLine({
      kind: 'discount',
      code: 'child_discount',
      label: 'fee.child_discount',
      nameJson: childBandLabel(band),
      qty: bucket.personNights,
      unitPrice: -fromCents(roundCents(bucket.cents / bucket.personNights)),
      amountCents: -bucket.cents,
      vatRate: accVat,
      inclusive: pricesIncludeVat,
    }))
  }

  for (const bucket of [...extraBuckets.values()].sort((a, b) => b.member.factor - a.member.factor)) {
    if (bucket.personNights === 0) continue
    const band = bucket.member.band
    lines.push(makeLine({
      kind: 'extra',
      code: band ? 'extra_person_child' : 'extra_person',
      label: band ? 'fee.extra_person_child' : 'fee.extra_person',
      ...(band ? { nameJson: childBandLabel(band) } : {}),
      qty: bucket.personNights,
      unitPrice: fromCents(roundCents(bucket.cents / bucket.personNights)),
      amountCents: bucket.cents,
      vatRate: accVat,
      inclusive: pricesIncludeVat,
    }))
  }

  // ---- 3: fees
  const applicable = fees
    .filter((f) => f.active && (f.unit_id === null || f.unit_id === unit.id))
    .filter((f) => f.code !== 'tourist_tax')   // handled below, from the tax rule
    .filter((f) => f.mandatory || optionalFeeIds.includes(f.id))
    .sort((a, b) => a.sort_order - b.sort_order)

  /*
   * Grundlage für prozentuale Nebenkosten: was der Gast für den Aufenthalt selbst
   * zahlt. Beide Nachlässe gehören abgezogen — der nach Alter gestaffelte wie der vom
   * Vermieter gewährte. Eine Prozentgebühr auf einen Betrag, den der Gast nie zahlt,
   * wäre zu hoch, und der Sonderpreis steckt ohnehin schon in `accommodationCents`.
   *
   * Nie unter null: ein Nachlass, der größer ist als der Aufenthalt, ergäbe sonst eine
   * negative Servicegebühr — also eine Gutschrift, die niemand versprochen hat.
   */
  const discountCents = discount > 0 ? toCents(discount) : 0
  const accommodationTotalCents = Math.max(
    0, accommodationCents + extraPersonCents - childDiscountCents - discountCents)

  for (const fee of applicable) {
    const qty = feeQty(fee.basis, Math.min(nightCount, fee.max_nights ?? nightCount), persons)
    const amountCents =
      fee.basis === 'percent'
        ? roundCents(accommodationTotalCents * (fee.amount / 100))
        : toCents(fee.amount) * qty
    if (amountCents === 0) continue
    lines.push(makeLine({
      kind: 'fee',
      code: fee.code,
      label: `fee:${fee.code}`,
      qty,
      unitPrice: fee.basis === 'percent' ? fromCents(amountCents) : fee.amount,
      amountCents,
      vatRate: noVat ? 0 : fee.vat_rate,
      inclusive: pricesIncludeVat,
      nameJson: fee.name,
    }))
  }

  // ---- 4: tourist tax
  if (touristTax && touristTax.amount_per_person_night > 0) {
    const { liable, agesUnknown } = liableGuests(
      adults, childrenAges, children, touristTax.exempt_under_age,
    )
    if (agesUnknown) warnings.push({ code: 'unknown_child_ages' })
    const taxNights = Math.min(nightCount, touristTax.max_nights ?? nightCount)
    const qty = liable * taxNights
    if (qty > 0) {
      lines.push(makeLine({
        kind: 'fee',
        code: 'tourist_tax',
        label: 'fee:tourist_tax',
        qty,
        unitPrice: touristTax.amount_per_person_night,
        amountCents: toCents(touristTax.amount_per_person_night) * qty,
        // The levy is passed through to the municipality; its VAT treatment is
        // set on the rule, not by the tenant's accommodation rate.
        vatRate: noVat ? 0 : touristTax.vat_rate,
        inclusive: pricesIncludeVat,
        nameJson: touristTax.name,
      }))
    }
  }

  // ---- 5: discount
  if (discountCents > 0) {
    const reason = discountReason?.trim()
    lines.push(makeLine({
      kind: 'discount',
      code: 'discount',
      label: discountLabel,
      // Derselbe Text in beiden Sprachen: der Grund ist getippt, nicht übersetzt.
      // Er in beide Fächer zu legen ist ehrlicher, als die englische Rechnung auf ein
      // leeres Feld und von dort auf „Discount" zurückfallen zu lassen — der Gast hat
      // „Gutschein" zugesagt bekommen und soll „Gutschein" lesen.
      ...(reason ? { nameJson: { de: reason, en: reason } } : {}),
      qty: 1,
      unitPrice: -discount,
      amountCents: -discountCents,
      vatRate: accVat,
      inclusive: pricesIncludeVat,
    }))
  }

  const net = lines.reduce((a, l) => a + toCents(l.net), 0)
  const vat = lines.reduce((a, l) => a + toCents(l.vat), 0)

  return {
    nights: pricedNights,
    lines,
    nightCount,
    averageNightly: avgNightly,
    listAverageNightly: fromCents(roundCents(listCents / nightCount)),
    nightlyOverride,
    totals: { net: fromCents(net), vat: fromCents(vat), gross: fromCents(net + vat) },
    warnings,
  }
}

/**
 * `amountCents` is what the landlord typed. Whether that figure is net or gross is the
 * tenant's setting, and the split happens here — once, for every kind of line — so
 * the two modes cannot drift apart line by line.
 */
function makeLine(args: {
  kind: LineKind
  code: string
  label: string
  qty: number
  unitPrice: number
  /** What the landlord typed: net or gross, according to `inclusive`. */
  amountCents: number
  vatRate: number
  /** The amount already contains VAT and must be split out of it. */
  inclusive?: boolean
  nameJson?: Record<string, string>
}): PriceLine {
  const { net, vat } = args.inclusive
    ? splitGross(args.amountCents, args.vatRate)
    : { net: args.amountCents, vat: vatOf(args.amountCents, args.vatRate) }
  return {
    kind: args.kind,
    code: args.code,
    label: args.label,
    qty: args.qty,
    unitPrice: args.unitPrice,
    net: fromCents(net),
    vatRate: args.vatRate,
    vat: fromCents(vat),
    gross: fromCents(net + vat),
    ...(args.nameJson ? { nameJson: args.nameJson } : {}),
  }
}

/**
 * Wie der Betrieb seine Anzahlung bemisst.
 *
 * Zwei Modelle sind üblich und schließen einander aus: ein Anteil am Gesamtpreis
 * („30 %") oder immer derselbe Betrag („200 €"). Wer Wochenpauschalen verkauft,
 * nimmt fast immer das zweite — der Gast liest bei jeder Buchung dieselbe Zahl und
 * muss nicht nachrechnen.
 */
export interface PrepaymentPolicy {
  mode: 'percent' | 'fixed' | 'none'
  /** Nur bei `percent`. */
  percent: number
  /** Nur bei `fixed`. */
  fixed: number
}

export const DEFAULT_PREPAYMENT: PrepaymentPolicy = { mode: 'percent', percent: 30, fixed: 0 }

/**
 * Die vorgeschlagene Anzahlung für einen Aufenthalt.
 *
 * Auf ganze Währungseinheiten gerundet: „€ 234,60 Anzahlung" liest sich wie ein
 * Rechenfehler, „€ 235" wie eine Bedingung.
 *
 * Nie mehr als der Gesamtpreis — ein fixer Betrag von 200 € auf eine Nacht für 90 €
 * wäre sonst eine Anzahlung, die höher ist als die Rechnung, und der Gast bekäme am
 * Ende Geld zurück statt eine Zahlungsaufforderung.
 */
export function prepaymentFor(grossTotal: number, policy: PrepaymentPolicy | number): number {
  // Die alte Aufrufform (nur ein Prozentsatz) bleibt gültig: sie steht noch in
  // Tests und in Aufrufen, die keinen Betrieb zur Hand haben.
  const p: PrepaymentPolicy = typeof policy === 'number'
    ? { mode: 'percent', percent: policy, fixed: 0 }
    : policy

  if (p.mode === 'none' || grossTotal <= 0) return 0
  const raw = p.mode === 'fixed'
    ? p.fixed
    : grossTotal * (p.percent / 100)
  if (raw <= 0) return 0
  return Math.min(grossTotal, Math.round(raw))
}

/** Liest die Anzahlungsregel aus den Stammdaten, mit den Vorgaben als Rückfall. */
export function prepaymentPolicyOf(t: {
  prepayment_mode?: string | null
  prepayment_percent?: number | null
  prepayment_fixed?: number | null
}): PrepaymentPolicy {
  const mode = t.prepayment_mode === 'fixed' || t.prepayment_mode === 'none'
    ? t.prepayment_mode
    : 'percent'
  return {
    mode,
    percent: t.prepayment_percent ?? DEFAULT_PREPAYMENT.percent,
    fixed: t.prepayment_fixed ?? 0,
  }
}

/** Ages of children at arrival, derived from birthdates where they are known. */
export function childAgesAtArrival(birthdates: (string | null | undefined)[], arrival: ISODate): number[] {
  return birthdates.filter((b): b is string => !!b).map((b) => ageOn(b, arrival))
}
